import { Icon } from '@assembly-js/design-system';
import { useTranslation } from 'react-i18next';
import {
  FiExternalLink as OpenPortalIcon,
  FiNavigation as NavigationIcon,
} from 'react-icons/fi';

import {
  COACHMARK_HIGHLIGHT_CLASS,
  COACHMARK_MENU_PROPS,
} from 'src/components/Coachmark';
import { TrashIcon } from 'src/components/icons';
import { useFeatureFlags } from 'src/context';
// eslint-disable-next-line no-restricted-imports
import { Action, BaseActionsMenuButton } from 'src/legacy/components/Dropdowns';

import { ClientActionsRendererProps } from './types';

export const ClientActionRenderer = ({
  data,
  handleClickRemove,
  handleClickEdit,
  handleClickInvite,
  handleClickCopyAccessLink,
  handleClickEmailAccessLink,
  handleClickViewDetails,
  handleClickTestAsClient,
  onActionMenuClick,
  isCoachmarkActive = false,
  onOpenPortalCoachmark,
}: ClientActionsRendererProps) => {
  const { t } = useTranslation();

  // The flag drops the redundant "View details" entry, since a row click already
  // opens it. It gates on the flag alone, not on Studio workspaces, so classic
  // workspaces get the cleanup too.
  const { EnableStudioFTUX } = useFeatureFlags();

  const actions: Action[] = [
    {
      name: t(($) => $.actions.edit_client),
      icon: <Icon icon="Edit" className="h-4 w-4" />,
      onClick: () => handleClickEdit(data),
    },
  ];
  if (data.status !== 'Active' && handleClickInvite) {
    actions.push({
      name: `${
        data.status === 'Inactive'
          ? 'Send invite'
          : t(($) => $.actions.invite_client)
      }...`,
      onClick: () => handleClickInvite(data),
      icon: <NavigationIcon />,
    });
  }

  if (handleClickEmailAccessLink) {
    actions.push({
      name: `Email magic link`,
      onClick: () => handleClickEmailAccessLink(data),
      icon: <Icon icon="Invite" className="h-4 w-4" />,
    });
  }
  if (handleClickCopyAccessLink) {
    actions.push({
      name: `Copy magic link`,
      onClick: () => handleClickCopyAccessLink(data),
      icon: <Icon icon="Copy" className="h-4 w-4" />,
    });
  }

  if (handleClickViewDetails && !EnableStudioFTUX) {
    actions.unshift({
      name: 'View details',
      icon: <Icon icon="File" className="h-4 w-4" />,
      onClick: () => handleClickViewDetails(data),
    });
  }
  actions.push({
    name: t(($) => $.actions.delete_client),
    onClick: () => handleClickRemove(data),
    icon: <TrashIcon />,
    isDelete: true,
  });

  // "Sign in as client" rides the same flag so classic workspaces can preview the
  // client experience. The coachmark presentation on top of it — lit item, every
  // real action dimmed — only applies while the Studio tour's menu step is active on
  // the first row (the one the tour opens); clicking it then completes the tour.
  const showOpenPortal = EnableStudioFTUX;
  const menuActions: Action[] = showOpenPortal
    ? [
        {
          name: t(($) => $.crm.coachmark.menu.sign_in_as),
          icon: <OpenPortalIcon />,
          onClick: () => {
            handleClickTestAsClient?.(data);
            onOpenPortalCoachmark?.();
          },
          classes: isCoachmarkActive ? [COACHMARK_HIGHLIGHT_CLASS] : [],
        },
        ...actions.map((action) => ({
          ...action,
          isDisabled: isCoachmarkActive,
        })),
      ]
    : actions;

  return (
    <BaseActionsMenuButton
      onActionMenuClick={onActionMenuClick}
      actions={menuActions}
      menuPropsOverride={
        showOpenPortal && isCoachmarkActive ? COACHMARK_MENU_PROPS : undefined
      }
    />
  );
};
